import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Switch,
  VStack,
  Divider,
  useColorModeValue,
  FormControl,
  FormLabel,
  useToast,
  Skeleton,
} from "@chakra-ui/react";
import { fetchNotificationPreferences, updateNotificationPreferences } from "../../api/accountSettings";

export default function NotificationsSettings() {
  const bg = useColorModeValue("white", "gray.700");
  const toast = useToast();

  const [settings, setSettings] = useState({
    userAccountNotifications: true, // default true
    notifyFormSubmissions: false,
    newsletter: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  useEffect(() => {
    const fetchPreferences = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchNotificationPreferences();
        if (res && res.success && res.data) {
          setSettings({
            userAccountNotifications: res.data.user_account_notifications,
            notifyFormSubmissions: res.data.submitted_forms_notifications,
            newsletter: res.data.newsletter_subscription,
          });
        }
      } catch (err) {
        setError("Failed to load notification preferences.");
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  const handleToggle = async (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    setSaving(true);
    setSaveMessage("");
    try {
      // Prepare payload as expected by backend
      const payload = {
        user_account_notifications: newSettings.userAccountNotifications,
        submitted_forms_notifications: newSettings.notifyFormSubmissions,
        newsletter_subscription: newSettings.newsletter,
      };
      const res = await updateNotificationPreferences(payload);
      if (res && res.success) {
        setSaveMessage("Preferences updated successfully.");
        toast({
          title: res.message || "Preferences updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        setSaveMessage("Failed to update preferences.");
        toast({
          title: (res && res.message) || "Failed to update preferences.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      setSaveMessage("Failed to update preferences.");
      toast({
        title: "Failed to update preferences.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p={8}>
        <VStack spacing={6} align="stretch">
          <Skeleton height="32px" width="200px" />
          <Skeleton height="24px" width="100%" />
          <Skeleton height="32px" width="200px" />
          <Skeleton height="24px" width="100%" />
          <Skeleton height="32px" width="200px" />
          <Skeleton height="24px" width="100%" />
        </VStack>
      </Box>
    );
  }

  if (error) {
    return <Box p={8} color="red.500">{error}</Box>;
  }

  // Show saving or saved message
  const feedback = saving
    ? "Saving..."
    : saveMessage;

  return (
    <Box flex="1" h="92vh" overflowY="auto" p={8} pt={6}>
      <Heading size="md" mb={4}>
        Notifications
      </Heading>
      <Divider mb={8} />
      {feedback && (
        <Box mb={4} color={saving ? "gray.500" : saveMessage.includes("success") ? "green.500" : "red.500"}>
          {feedback}
        </Box>
      )}
      {/* Notification Preferences */}
      <VStack spacing={6} align="stretch" mb={10}>
        <FormControl
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <FormLabel mb={1}>Username or Password Changes</FormLabel>
            <Text fontSize="sm" color="gray.500">
              You’ll always receive security-related emails
            </Text>
          </Box>
          <Switch isChecked={settings.userAccountNotifications} />
        </FormControl>

        <FormControl
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <FormLabel mb={1}>Feedback / Problem Reports</FormLabel>
            <Text fontSize="sm" color="gray.500">
              Get notified when you submit a form
            </Text>
          </Box>
          <Switch
            isChecked={settings.notifyFormSubmissions}
            onChange={() => handleToggle("notifyFormSubmissions")}
          />
        </FormControl>

        <FormControl
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <FormLabel mb={1}>Newsletter Subscription</FormLabel>
            <Text fontSize="sm" color="gray.500">
              Stay updated with product tips and news
            </Text>
          </Box>
          <Switch
            isChecked={settings.newsletter}
            onChange={() => handleToggle("newsletter")}
          />
        </FormControl>
      </VStack>
    </Box>
  );
}
